/**
 * CR316: Recent Searches Configuration Section
 * 
 * Controls for recent search behavior (client-side storage).
 * Settings are stored in layout_json; actual searches are per-user in localStorage.
 */

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Clock } from "lucide-react";
import type { RecentSearchesConfig } from "@/lib/layout-studio/types";
import { DEFAULT_RECENT_SEARCHES_CONFIG } from "@/lib/layout-studio/types";

interface RecentSearchesSectionProps {
  config: RecentSearchesConfig;
  onChange: (config: RecentSearchesConfig) => void;
}

export function RecentSearchesSection({ config, onChange }: RecentSearchesSectionProps) {
  const mergedConfig = { ...DEFAULT_RECENT_SEARCHES_CONFIG, ...config };

  const handleToggle = (enabled: boolean) => {
    onChange({ ...mergedConfig, enabled });
  };

  const handleMaxItemsChange = (value: number) => {
    onChange({ ...mergedConfig, maxItems: value });
  };

  const handleShowWhenEmptyToggle = (showWhenEmpty: boolean) => {
    onChange({ ...mergedConfig, showWhenEmpty });
  };

  const handleShowWhenFocusedToggle = (showWhenFocused: boolean) => {
    onChange({ ...mergedConfig, showWhenFocused });
  };

  const handleShowClearAllToggle = (showClearAll: boolean) => {
    onChange({ ...mergedConfig, showClearAll });
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Recent Searches
          </CardTitle>
          <Switch
            checked={mergedConfig.enabled}
            onCheckedChange={handleToggle}
            aria-label="Enable recent searches"
          />
        </div>
        <CardDescription>
          Show users their recent search terms for quick access.
        </CardDescription>
      </CardHeader>
      {mergedConfig.enabled && (
        <CardContent className="space-y-4">
          {/* Max Items */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs">Max Recent Items</Label>
              <span className="text-[10px] text-muted-foreground tabular-nums">
                {mergedConfig.maxItems}
              </span>
            </div>
            <Slider
              value={[mergedConfig.maxItems]}
              onValueChange={([v]) => handleMaxItemsChange(v)}
              min={3}
              max={15}
              step={1}
              className="w-full"
            />
          </div>

          {/* Display Options */}
          <div className="space-y-3 pt-2 border-t">
            <div className="flex items-center justify-between">
              <Label className="text-xs">Show When Empty</Label>
              <Switch
                checked={mergedConfig.showWhenEmpty}
                onCheckedChange={handleShowWhenEmptyToggle}
              />
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-xs">Show When Focused</Label>
              <Switch
                checked={mergedConfig.showWhenFocused}
                onCheckedChange={handleShowWhenFocusedToggle}
              />
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-xs">Show "Clear All" Action</Label>
              <Switch
                checked={mergedConfig.showClearAll}
                onCheckedChange={handleShowClearAllToggle}
              />
            </div>
          </div>

          {/* Info */}
          <p className="text-[10px] text-muted-foreground pt-2 border-t">
            Recent searches are stored per user in their browser (localStorage), scoped by preset ID.
          </p>
        </CardContent>
      )}
    </Card>
  );
}
